import { Component, input } from '@angular/core';
import { UwIcon } from '../../ui/icon/uw-icon';
import { UwBadge } from '../../ui/badge/uw-badge';
import { UwProgress } from '../../ui/progress/uw-progress';
import type { RiskEvidence } from '../../../core/models/evidence.model';

@Component({
  selector: 'uw-risk-evidence',
  standalone: true,
  imports: [UwIcon, UwBadge, UwProgress],
  template: `
    @if (risk(); as r) {
      <div class="space-y-3">
        <div class="flex items-center justify-between rounded-lg border border-line bg-surface p-3">
          <div class="flex items-center gap-2.5">
            <span class="flex h-8 w-8 items-center justify-center rounded-lg bg-ai-soft text-ai">
              <uw-icon name="gauge" [size]="16" />
            </span>
            <div>
              <p class="text-[12.5px] font-semibold text-primary">{{ r.riskLabel }}</p>
              <p class="text-[10.5px] text-text-muted">{{ r.ruleId }} · computed 3 min ago</p>
            </div>
          </div>
          <uw-badge [tone]="r.statusTone">Composite {{ factorScore(r) }}</uw-badge>
        </div>

        <div class="space-y-2.5">
          @for (factor of r.factors; track factor.label) {
            <div>
              <div class="mb-1 flex items-center justify-between text-[11px]">
                <span class="text-secondary">{{ factor.label }}</span>
                <span class="typo-mono font-semibold" [class]="toneClass(factor.tone)">{{ factor.score }}</span>
              </div>
              <uw-progress [value]="factor.score" [tone]="factor.tone" [label]="factor.label" />
            </div>
          }
        </div>

        <p class="flex items-center gap-1.5 rounded-lg bg-surface-sunken px-3 py-2 text-[10.5px] leading-snug text-text-muted">
          <uw-icon name="zap" [size]="12" class="shrink-0 text-ai" />
          Scores range 0–100; higher is better. Composite reflects the weighted Risk Assessment Engine model.
        </p>
      </div>
    }
  `,
})
export class UwRiskEvidence {
  readonly risk = input<RiskEvidence | null>(null);

  protected factorScore(r: RiskEvidence): number {
    if (!r.factors.length) return 0;
    return Math.round(r.factors.reduce((s, f) => s + f.score, 0) / r.factors.length);
  }

  protected toneClass(tone: string): string {
    const tones: Record<string, string> = {
      success: 'text-success',
      warning: 'text-warning',
      danger: 'text-danger',
      info: 'text-info',
    };
    return tones[tone] ?? 'text-secondary';
  }
}
